import { createContext, useContext, useState, useEffect } from 'react';
import api from '@/lib/api';
import { clearStoredTokens, getStoredToken, storeToken } from '@/lib/authStorage';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(false);
  const [originalToken, setOriginalToken] = useState(null);

  // ── Renovacion de la sesion por actividad ────────────────────────────────
  // El pase caduca a los 40 minutos SIN ACTIVIDAD. Para que eso signifique
  // "inactividad" y no "tiempo desde que entro", la pantalla reinicia el reloj
  // mientras la persona usa el sistema.
  //
  // Se renueva como mucho cada 10 minutos aunque haya mil clics: no tiene
  // sentido pedir un pase nuevo en cada movimiento del raton.
  //
  // Si la renovacion falla NO se cierra la sesion aqui. El pase actual sigue
  // sirviendo hasta que caduque, y si ya caduco el interceptor de `api` manda a
  // la pantalla de entrada. Cerrar sesion por un fallo de red seria echar a
  // alguien que si estaba trabajando.
  useEffect(() => {
    if (!user) return;
    const CADA_MS = 10 * 60 * 1000;
    let ultima = Date.now();
    let vivo = true;

    const renovarSesion = async () => {
      if (!vivo || Date.now() - ultima < CADA_MS) return;
      ultima = Date.now();
      try {
        const r = await api.post('/auth/refresh');
        if (r.data?.token) {
          storeToken(r.data.token);
          api.defaults.headers.common['Authorization'] = `Bearer ${r.data.token}`;
        }
      } catch { /* el pase actual sigue valido hasta que caduque */ }
    };

    const eventos = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    eventos.forEach((e) => window.addEventListener(e, renovarSesion, { passive: true }));
    return () => {
      vivo = false;
      eventos.forEach((e) => window.removeEventListener(e, renovarSesion));
    };
  }, [user]);

  useEffect(() => {
    const token = getStoredToken();
    const impToken = localStorage.getItem('original_token');
    if (impToken) setOriginalToken(impToken);
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      api.get('/auth/me').then(r => {
        setUser(r.data);
        setImpersonating(!!impToken);
        setLoading(false);
      }).catch(() => { clearStoredTokens(); localStorage.removeItem('original_token'); setLoading(false); });
    } else setLoading(false);
  }, []);

  const login = async (email, password, extraPayload = null) => {
    const payload = extraPayload || { email, password };
    const r = await api.post('/auth/login', payload);
    storeToken(r.data.token);
    api.defaults.headers.common['Authorization'] = `Bearer ${r.data.token}`;
    const me = await api.get('/auth/me');
    setUser(me.data);
    return me.data;
  };

  const register = async (data) => {
    const r = await api.post('/auth/register', data);
    storeToken(r.data.token);
    api.defaults.headers.common['Authorization'] = `Bearer ${r.data.token}`;
    const me = await api.get('/auth/me');
    setUser(me.data);
    return me.data;
  };

  const impersonate = async (companyId) => {
    const currentToken = getStoredToken();
    const r = await api.post(`/admin/impersonate/${companyId}`);
    localStorage.setItem('original_token', currentToken);
    storeToken(r.data.token);
    api.defaults.headers.common['Authorization'] = `Bearer ${r.data.token}`;
    const me = await api.get('/auth/me');
    setUser(me.data);
    setImpersonating(true);
    setOriginalToken(currentToken);
    return me.data;
  };

  const stopImpersonation = async () => {
    const orig = localStorage.getItem('original_token');
    if (orig) {
      storeToken(orig);
      localStorage.removeItem('original_token');
      api.defaults.headers.common['Authorization'] = `Bearer ${orig}`;
      const me = await api.get('/auth/me');
      setUser(me.data);
      setImpersonating(false);
      setOriginalToken(null);
      return me.data;
    }
    return null;
  };

  const logout = () => {
    clearStoredTokens();
    localStorage.removeItem('original_token');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    setImpersonating(false);
    setOriginalToken(null);
  };

  const refreshUser = async () => {
    try {
      const me = await api.get('/auth/me');
      setUser(me.data);
    } catch {}
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, impersonate, stopImpersonation, impersonating, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
